import { generateId } from "@fleet/utils";
/* oxlint-disable lingui/no-unlocalized-strings -- TODO(i18n): translate (debt) */
import { createRoute, useNavigate, useSearch } from "@tanstack/solid-router";
import { createEffect, createMemo, createSignal, For, Show, untrack } from "solid-js";
import { chronosRoute } from "../chronos";
import { GlassSurface, PageContainer, PageHeader } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Check, Folder, History, Link2, Plus, Search } from "@fleet/icons";
import { canonicalUrl } from "@/lib/canonical-link";
import { ProjectTasksDialog } from "@/features/chronos-sync/components/project-tasks-dialog";
import { ProjectCard } from "@/features/chronos-sync/components/project-card";
import { ProjectVersionHistorySheet } from "@/features/chronos-sync/components/project-version-history-sheet";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { createPowerSyncQuery } from "@time-tracker/shared/solid";
import { ensureWorkspace } from "@/features/chronos-sync/workspace";
import { useWorkspace } from "@/features/chronos-sync/workspace-context";
import { WorkspaceSwitcher } from "@/features/chronos-sync/components/workspace-switcher";
import {
  and,
  eq,
  project,
  type Project,
  project_member,
  type ProjectVersionRecord,
  sql,
  time_entry,
  type TimeEntry,
  user,
} from "@time-tracker/powersync";
import { showToast } from "@/components/ui/toast";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import {
  cn,
  liveProjectFilters,
  liveProjectMemberFilters,
  liveTimeEntryFilters,
  patchRowVerified,
  queryKeys,
  restoreProjectVersionMutation,
} from "@time-tracker/shared";
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import { ProjectsEmptyIllustration } from "@/components/ui/illustrations";

// Named constants to avoid magic numbers
const DAYS_IN_WEEK = 7;
const HOURS_IN_DAY = 24;
const MINS_IN_HOUR = 60;
const SECS_IN_MIN = 60;
const MS_IN_SEC = 1000;
const MIN_CODE_LENGTH = 2;
const MAX_CODE_LENGTH = 5;

const COLOR_OPTIONS = [
  { value: "bg-blue-500", label: "Electric Blue", color: "#afc6ff" },
  { value: "bg-purple-500", label: "Vivid Violet", color: "#ebb2ff" },
  { value: "bg-emerald-500", label: "Neon Mint", color: "#ceffdf" },
  { value: "bg-amber-500", label: "Gold", color: "#f5a623" },
];

// Reconstructs the project route's own search from the global search
// (merged by the router), narrowing the fields to their literals. It avoids carrying
// `dialog` from other screens (e.g. "entry-new") and preserves `ws`/`wsdialog`.
function carryProjectsSearch(prev: {
  ws?: string;
  wsdialog?: unknown;
  dialog?: unknown;
  confirm?: unknown;
  id?: string;
  q?: string;
}): ProjectsSearch {
  const dialog =
    prev.dialog === "new" || prev.dialog === "edit" || prev.dialog === "tasks"
      ? prev.dialog
      : undefined;
  return {
    ws: prev.ws,
    wsdialog: prev.wsdialog === "new" ? "new" : undefined,
    dialog,
    confirm: prev.confirm === "delete" ? "delete" : undefined,
    id: prev.id,
    q: prev.q,
  };
}

function ProjectsPage() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  const { activeWorkspaceId } = useWorkspace();
  const { t } = useI18n();

  // Deep-link support: the project-search filter is shareable via ?q=. Reading
  // the URL on load reconstructs the filter; typing writes it back (replace, so
  // we don't flood history). An effect mirrors external URL changes (back/forward
  // or a pasted link) into the signal → idempotent on reload.
  const search = useSearch({ from: chronosProjectsRoute.id });
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = createSignal(search().q ?? "");
  const [copied, setCopied] = createSignal(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(canonicalUrl());
      setCopied(true);
      showToast(t(msg`Link copied`), "success");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast(t(msg`Couldn't copy the link`), "error");
    }
  };

  const updateSearchQuery = (value: string) => {
    setSearchQuery(value);
    const q = value.trim();
    void navigate({
      to: chronosProjectsRoute.to,
      // Preserve `ws`/`wsdialog` and the active dialog; only touch `q`.
      search: (prev) => ({ ...carryProjectsSearch(prev), q: q === "" ? undefined : q }),
      replace: true,
    });
  };

  // Comparison in `untrack`: only reacts to EXTERNAL changes to `?q=`; if
  // it tracked `searchQuery()` (the signal it writes), typing re-fires it with the
  // OLD URL and reverts the input (bug #15b / CC-16).
  createEffect(() => {
    const urlQuery = search().q ?? "";
    if (untrack(() => urlQuery !== searchQuery())) {
      setSearchQuery(urlQuery);
    }
  });
  const [isOpen, setIsOpen] = createSignal(false);
  const [projectName, setProjectName] = createSignal("");
  const [projectCode, setProjectCode] = createSignal("");
  const [projectColor, setProjectColor] = createSignal("bg-blue-500");
  const [projectStatus, setProjectStatus] = createSignal("active");
  // PROJECT VERSIONING / rate-sealing: billing definition persisted into the
  // SYNCED project (not localStorage) so future time entries seal a real rate.
  const [projectHourlyRate, setProjectHourlyRate] = createSignal("");
  const [projectCurrency, setProjectCurrency] = createSignal("USD");
  const [projectBillable, setProjectBillable] = createSignal(true);
  const [editingProjectId, setEditingProjectId] = createSignal<string | null>(null);
  const [versionSheetOpen, setVersionSheetOpen] = createSignal(false);
  const [restoringVersion, setRestoringVersion] = createSignal(false);
  const [isSubmitting, setIsSubmitting] = createSignal(false);
  const [openMenuId, setOpenMenuId] = createSignal<string | null>(null);
  const [deleteTarget, setDeleteTarget] = createSignal<Project | null>(null);
  const [tasksProject, setTasksProject] = createSignal<Project | null>(null);

  // Dialog deep-links: each modal has its own copyable URL.
  //   - Create project      → ?dialog=new
  //   - Edit project        → ?dialog=edit&id=<projectId>
  //   - Project tasks       → ?dialog=tasks&id=<projectId>
  //   - Confirm deletion    → ?confirm=delete&id=<projectId>
  // We write to the URL when opening/closing and an effect hydrates state from the
  // URL (pasted links, back/forward). Idempotent on reload.
  const setDialogUrl = (params: {
    dialog?: "new" | "edit" | "tasks";
    confirm?: "delete";
    id?: string;
  }) => {
    void navigate({
      to: chronosProjectsRoute.to,
      search: (prev) => ({
        ...carryProjectsSearch(prev),
        dialog: params.dialog,
        confirm: params.confirm,
        id: params.id,
      }),
      replace: true,
    });
  };

  const openCreateDialog = () => {
    setEditingProjectId(null);
    setProjectName("");
    setProjectCode("");
    setProjectColor("bg-blue-500");
    setProjectStatus("active");
    setProjectHourlyRate("");
    setProjectCurrency("USD");
    setProjectBillable(true);
    setIsOpen(true);
    setDialogUrl({ dialog: "new" });
  };

  const openEditDialog = (p: Project) => {
    setEditingProjectId(p.id);
    setProjectName(p.name ?? "");
    setProjectCode(p.code ?? "");
    setProjectColor(p.color ?? "bg-blue-500");
    setProjectStatus(p.status ?? "active");
    setProjectHourlyRate(
      p.hourly_rate === null || p.hourly_rate === undefined ? "" : String(p.hourly_rate),
    );
    setProjectCurrency(p.currency ?? "USD");
    setProjectBillable(
      p.billable === null || p.billable === undefined ? true : Boolean(p.billable),
    );
    setOpenMenuId(null);
    setIsOpen(true);
    setDialogUrl({ dialog: "edit", id: p.id });
  };

  const toggleArchive = async (p: Project) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    setOpenMenuId(null);
    const nextStatus = p.status === "completed" ? "active" : "completed";
    const name = p.name ?? "";
    try {
      await patchRowVerified(dbInstance, project, project.id, p.id, { status: nextStatus });
      showToast(
        nextStatus === "completed"
          ? t(msg`Project "${name}" archived`)
          : t(msg`Project "${name}" reactivated`),
        "success",
      );
    } catch (err) {
      console.error("Error al archivar proyecto:", err);
      showToast(t(msg`Could not update the project`), "error");
    }
  };

  const deleteTargetName = () => deleteTarget()?.name ?? "";

  const handleDeleteConfirmed = async () => {
    const target = deleteTarget();
    const dbInstance = drizzle();
    if (!target || !dbInstance) {
      return;
    }
    const name = target.name ?? "";
    try {
      await patchRowVerified(dbInstance, project, project.id, target.id, {
        deleted_at: new Date().toISOString(),
      });
      showToast(t(msg`Project "${name}" deleted`), "success");
    } catch (err) {
      console.error("Error al eliminar proyecto:", err);
      showToast(t(msg`Could not delete the project`), "error");
    } finally {
      setDeleteTarget(null);
      setDialogUrl({});
    }
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    const dbInstance = drizzle();
    const currentUserId = userId();
    if (!dbInstance || !currentUserId) {
      showToast(t(msg`Database not ready`), "error");
      return;
    }

    const nameStr = projectName().trim();
    const codeStr = projectCode().trim().toUpperCase();

    if (!nameStr) {
      showToast(t(msg`Please enter the project name`), "warning");
      return;
    }
    if (!codeStr || codeStr.length < MIN_CODE_LENGTH || codeStr.length > MAX_CODE_LENGTH) {
      showToast(t(msg`The code must be 2-5 characters`), "warning");
      return;
    }

    // Billing definition → synced project (rate-sealing source of truth).
    const rateNum = projectHourlyRate().trim() === "" ? null : Number(projectHourlyRate());
    const hourlyRate = rateNum !== null && Number.isFinite(rateNum) ? rateNum : null;
    const currency = projectCurrency().trim() || null;
    const billableInt = projectBillable() ? 1 : 0;

    setIsSubmitting(true);
    try {
      const editingId = editingProjectId();
      if (editingId) {
        await patchRowVerified(dbInstance, project, project.id, editingId, {
          name: nameStr,
          code: codeStr,
          color: projectColor(),
          status: projectStatus(),
          hourly_rate: hourlyRate,
          currency,
          billable: billableInt,
        });
        showToast(t(msg`Project "${nameStr}" updated`), "success");
      } else {
        const workspaceId =
          activeWorkspaceId() || (await ensureWorkspace(dbInstance, currentUserId));
        const newProjectId = generateId();

        await dbInstance.insert(project).values({
          id: newProjectId,
          workspace_id: workspaceId,
          name: nameStr,
          code: codeStr,
          color: projectColor(),
          status: "active",
          hourly_rate: hourlyRate,
          currency,
          billable: billableInt,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

        await dbInstance.insert(project_member).values({
          id: generateId(),
          project_id: newProjectId,
          user_id: currentUserId,
          created_at: new Date().toISOString(),
        });

        showToast(t(msg`Project "${nameStr}" created successfully`), "success");
      }
      setIsOpen(false);
      setEditingProjectId(null);
      setProjectName("");
      setProjectCode("");
      setProjectColor("bg-blue-500");
      setProjectStatus("active");
      setDialogUrl({});
    } catch (err) {
      console.error("Error al guardar proyecto:", err);
      showToast(t(msg`Could not save the project`), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Restore a billing version (PROJECT VERSIONING): forward write; the server
  // materializes the restored definition as a NEW version and existing time
  // entries keep their sealed rate.
  const handleRestoreVersion = async (version: ProjectVersionRecord) => {
    const dbInstance = drizzle();
    const editingId = editingProjectId();
    if (!dbInstance || !editingId) {
      return;
    }
    setRestoringVersion(true);
    try {
      await restoreProjectVersionMutation(dbInstance, editingId, {
        name: version.name,
        code: version.code,
        color: version.color,
        status: version.status,
        hourly_rate: version.hourly_rate,
        currency: version.currency,
        billable: version.billable,
      });
      setProjectName(version.name ?? "");
      setProjectCode(version.code ?? "");
      setProjectColor(version.color ?? "bg-blue-500");
      setProjectStatus(version.status ?? "active");
      setProjectHourlyRate(
        version.hourly_rate === null || version.hourly_rate === undefined
          ? ""
          : String(version.hourly_rate),
      );
      setProjectCurrency(version.currency ?? "USD");
      setProjectBillable(
        version.billable === null || version.billable === undefined
          ? true
          : Boolean(version.billable),
      );
      setVersionSheetOpen(false);
      showToast(t(msg`Version restored`), "success");
    } catch (err) {
      console.error("Failed to restore version:", err);
      showToast(t(msg`Could not restore the version`), "error");
    } finally {
      setRestoringVersion(false);
    }
  };

  // 1. Query projects of the active workspace
  const projectsQuery = createPowerSyncQuery<Project>({
    queryKey: () => queryKeys.chronos.projectsList(userId(), activeWorkspaceId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(project)
        .where(and(eq(project.workspace_id, activeWorkspaceId()), ...liveProjectFilters()));
    },
    enabled: () => Boolean(drizzle()) && Boolean(activeWorkspaceId()),
    suspense: false,
  });

  // 2. Query all project members inner joined with user
  const projectMembersQuery = createPowerSyncQuery<{
    id: string;
    project_id: string | null;
    user_id: string | null;
    name: string | null;
    avatar: string | null;
  }>({
    queryKey: () => queryKeys.chronos.projectMembers(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select({
          id: project_member.id,
          project_id: project_member.project_id,
          user_id: project_member.user_id,
          name: user.name,
          avatar: user.image,
        })
        .from(project_member)
        .innerJoin(user, eq(project_member.user_id, user.id))
        .where(and(...liveProjectMemberFilters()));
    },
    enabled: () => Boolean(drizzle()),
    suspense: false,
  });

  // 3. Query weekly time entries (last 7 days) to aggregate weekly hours
  const weeklyEntriesQuery = createPowerSyncQuery<TimeEntry>({
    queryKey: () => queryKeys.chronos.weeklyEntriesProjects(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      const sevenDaysAgo = new Date(
        Date.now() - DAYS_IN_WEEK * HOURS_IN_DAY * MINS_IN_HOUR * SECS_IN_MIN * MS_IN_SEC,
      );
      return dbInstance
        .select()
        .from(time_entry)
        .where(
          and(
            eq(time_entry.user_id, userId()),
            sql`time_entry.start_time >= ${sevenDaysAgo.toISOString()}`,
            ...liveTimeEntryFilters(),
          ),
        );
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()),
    suspense: false,
  });

  // 4. Combine all sources and process data reactively
  const processedProjects = createMemo(() => {
    const list = projectsQuery.data ?? [];
    const members = projectMembersQuery.data ?? [];
    const entries = weeklyEntriesQuery.data ?? [];

    // Calculate weekly hours per project
    const weeklyMsMap = new Map<string, number>();
    for (const entry of entries) {
      if (!entry.project_id || !entry.start_time) {
        continue;
      }
      const start = new Date(entry.start_time).getTime();
      const end = entry.end_time ? new Date(entry.end_time).getTime() : Date.now();
      if (end > start) {
        weeklyMsMap.set(entry.project_id, (weeklyMsMap.get(entry.project_id) ?? 0) + (end - start));
      }
    }

    // Map members by project ID
    const projectMembersMap = new Map<string, typeof members>();
    for (const m of members) {
      if (!m.project_id) {
        continue;
      }
      const current = projectMembersMap.get(m.project_id) ?? [];
      current.push(m);
      projectMembersMap.set(m.project_id, current);
    }

    const result = [];
    for (const p of list) {
      const totalMs = weeklyMsMap.get(p.id) ?? 0;
      const totalHoursDecimal = totalMs / (MS_IN_SEC * SECS_IN_MIN * MINS_IN_HOUR);
      const hours = Math.floor(totalHoursDecimal);
      const mins = Math.floor((totalHoursDecimal - hours) * MINS_IN_HOUR);
      const durationStr = `${hours.toString().padStart(2, "0")}:${mins
        .toString()
        .padStart(2, "0")}`;

      result.push({
        ...p,
        durationStr,
        members: projectMembersMap.get(p.id) ?? [],
      });
    }
    return result;
  });

  // 5. Apply filtering
  const filteredProjects = createMemo(() => {
    const list = processedProjects();
    const query = searchQuery().toLowerCase().trim();
    if (!query) {
      return list;
    }
    return list.filter(
      (p) =>
        (p.name ?? "").toLowerCase().includes(query) ||
        (p.code ?? "").toLowerCase().includes(query) ||
        (p.status ?? "").toLowerCase().includes(query),
    );
  });

  // URL → dialog state. Hydrates the modal indicated by the URL. For edit /
  // tasks / delete we need the project row: we resolve it from the listing
  // (it syncs local-first); while it arrives, the absent entity simply
  // leaves the dialog closed until it appears. Idempotent: we do not reopen if it is
  // already in the correct state.
  createEffect(() => {
    const { dialog } = search();
    const { confirm } = search();
    const { id } = search();
    const list = processedProjects();
    const byId = (pid: string | undefined) =>
      pid ? (list.find((p) => p.id === pid) ?? null) : null;

    // CC-19/CC-16: state↔URL reconciliation runs in `untrack` so this
    // effect ONLY reacts to EXTERNAL URL changes (`search()`) or the
    // entity arriving via sync (`processedProjects()`), NEVER to the dialog's
    // local signals. If it tracked `isOpen()`/`tasksProject()`/`deleteTarget()`,
    // opening a dialog state-first (`setIsOpen(true)` before async `navigate`
    // renders `?dialog=`) would re-fire the effect with the OLD URL (without the
    // param) and fall into the close branch → the dialog BOUNCES closed instantly
    // (the same pattern already recorded in WorkspaceSwitcher). Closing via back/forward
    // still works: it changes `search()`, which IS tracked.
    untrack(() => {
      if (dialog === "new") {
        if (!isOpen() || editingProjectId() !== null) {
          openCreateDialogFromUrl();
        }
        return;
      }
      if (dialog === "edit" && id) {
        const proj = byId(id);
        if (proj && editingProjectId() !== id) {
          hydrateEditFromUrl(proj);
        }
        return;
      }
      if (dialog === "tasks" && id) {
        const proj = byId(id);
        if (proj && tasksProject()?.id !== id) {
          setTasksProject(proj);
        }
        return;
      }
      if (confirm === "delete" && id) {
        const proj = byId(id);
        if (proj && deleteTarget()?.id !== id) {
          setDeleteTarget(proj);
        }
        return;
      }
      // No dialog in the URL: close any that remains open (back/forward).
      if (isOpen()) {
        setIsOpen(false);
      }
      if (tasksProject()) {
        setTasksProject(null);
      }
      if (deleteTarget()) {
        setDeleteTarget(null);
      }
    });
  });

  // "From URL" variants that do NOT rewrite the URL (they avoid a loop with the effect).
  function openCreateDialogFromUrl() {
    setEditingProjectId(null);
    setProjectName("");
    setProjectCode("");
    setProjectColor("bg-blue-500");
    setProjectStatus("active");
    setProjectHourlyRate("");
    setProjectCurrency("USD");
    setProjectBillable(true);
    setIsOpen(true);
  }

  function hydrateEditFromUrl(p: Project) {
    setEditingProjectId(p.id);
    setProjectName(p.name ?? "");
    setProjectCode(p.code ?? "");
    setProjectColor(p.color ?? "bg-blue-500");
    setProjectStatus(p.status ?? "active");
    setProjectHourlyRate(
      p.hourly_rate === null || p.hourly_rate === undefined ? "" : String(p.hourly_rate),
    );
    setProjectCurrency(p.currency ?? "USD");
    setProjectBillable(
      p.billable === null || p.billable === undefined ? true : Boolean(p.billable),
    );
    setIsOpen(true);
  }

  return (
    <PageContainer
      maxWidth="7xl"
      class="space-y-8"
      containerClass="bg-[#000000] text-white pb-[calc(5.5rem+var(--safe-area-inset-bottom))]"
    >
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <PageHeader
          title={t(msg`Projects`)}
          description={t(msg`Manage active projects and weekly accumulated hours.`)}
          icon={Folder}
        />
        <div class="flex items-center gap-2">
          <WorkspaceSwitcher />
          {/* Search bar */}
          <div class="relative flex-1 md:w-64">
            <span class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search class="h-4 w-4 text-muted-foreground" />
            </span>
            <input
              type="text"
              placeholder={t(msg`Search project...`)}
              value={searchQuery()}
              onInput={(e) => updateSearchQuery(e.currentTarget.value)}
              class="w-full pl-9 pr-4 py-2 bg-[#121212] border border-white/10 rounded-[16px] text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCopyLink}
            class="border-white/10 hover:bg-white/5 flex items-center gap-2 rounded-full cursor-pointer"
            data-testid="copy-link-button"
          >
            <Show when={copied()} fallback={<Link2 class="h-4 w-4" />}>
              <Check class="h-4 w-4 text-emerald-500" />
            </Show>
            <span>{copied() ? t(msg`Link copied`) : t(msg`Copy link`)}</span>
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={openCreateDialog}
            class="bg-primary text-on-primary hover:bg-primary/90 flex items-center gap-2 font-semibold rounded-full cursor-pointer"
          >
            <Plus class="h-4 w-4" />
            {t(msg`New Project`)}
          </Button>
        </div>
      </div>

      <Show
        when={filteredProjects().length > 0}
        fallback={
          <GlassSurface class="p-12 text-center bg-[#121212] border border-white/10 rounded-[24px] shadow-none motion-safe:animate-fade-in">
            <ProjectsEmptyIllustration class="h-32 w-32 mx-auto mb-2" />
            <h3 class="text-sm font-bold text-foreground font-mono">{t(msg`No projects found`)}</h3>
            <p class="text-xs text-muted-foreground mt-1">
              {t(msg`Try changing the search filters or create a new one.`)}
            </p>
          </GlassSurface>
        }
      >
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <For each={filteredProjects()}>
            {(p, i) => (
              <div
                class="motion-safe:animate-slide-in-up motion-safe:opacity-0"
                style={{ "animation-delay": `${Math.min(i() * 45, 360)}ms` }}
              >
                <ProjectCard
                  project={p}
                  isMenuOpen={() => openMenuId() === p.id}
                  setMenuOpen={(open) => setOpenMenuId(open ? p.id : null)}
                  onEdit={openEditDialog}
                  onTasks={(proj) => {
                    setOpenMenuId(null);
                    setTasksProject(proj);
                    setDialogUrl({ dialog: "tasks", id: proj.id });
                  }}
                  onToggleArchive={(proj) => void toggleArchive(proj)}
                  onDelete={(proj) => {
                    setOpenMenuId(null);
                    setDeleteTarget(proj);
                    setDialogUrl({ confirm: "delete", id: proj.id });
                  }}
                />
              </div>
            )}
          </For>
        </div>
      </Show>

      {/* Dialog para crear un nuevo proyecto */}
      <Dialog
        open={isOpen()}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (!open) {
            setEditingProjectId(null);
            setDialogUrl({});
          }
        }}
        contentClass="bg-[#121212] border border-white/10 rounded-[24px] p-0 overflow-hidden max-w-md"
      >
        <div class="p-6 text-white">
          <DialogHeader showClose border={false} class="p-0 mb-4 text-white">
            <DialogTitle class="text-xl font-bold font-mono">
              {editingProjectId() ? t(msg`Edit Project`) : t(msg`New Project`)}
            </DialogTitle>
            <DialogDescription class="text-xs text-white/60 font-mono mt-1">
              {t(msg`Define the project identity to track time precisely.`)}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} class="space-y-5">
            <div class="space-y-1">
              <label
                for="new-project-name"
                class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
              >
                {t(msg`Project Name`)}
              </label>
              <input
                id="new-project-name"
                type="text"
                placeholder={t(msg`e.g. Stitch UX Redesign`)}
                value={projectName()}
                onInput={(e) => setProjectName(e.currentTarget.value)}
                class="w-full bg-black/40 border border-white/10 rounded-[16px] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#afc6ff]/40 text-white placeholder-white/20 font-mono"
              />
            </div>

            <div class="space-y-1">
              <label
                for="new-project-code"
                class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
              >
                {t(msg`Code (2-5 letters)`)}
              </label>
              <input
                id="new-project-code"
                type="text"
                placeholder="e.g. STITCH"
                value={projectCode()}
                onInput={(e) => setProjectCode(e.currentTarget.value)}
                class="w-full bg-black/40 border border-white/10 rounded-[16px] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#afc6ff]/40 text-white placeholder-white/20 uppercase font-mono"
              />
            </div>

            <Show when={editingProjectId()}>
              <div class="space-y-1">
                <label
                  for="edit-project-status"
                  class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
                >
                  {t(msg`Status`)}
                </label>
                <Select
                  id="edit-project-status"
                  aria-label={t(msg`Status`)}
                  value={projectStatus()}
                  onChange={setProjectStatus}
                  class="font-mono"
                  options={[
                    { value: "active", label: t(msg`Active`) },
                    { value: "paused", label: t(msg`Paused`) },
                    { value: "review", label: t(msg`In review`) },
                    { value: "completed", label: t(msg`Completed`) },
                  ]}
                />
              </div>
            </Show>

            <div class="space-y-2">
              <span class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono block">
                {t(msg`Accent Color`)}
              </span>
              <div class="flex items-center gap-3">
                <For each={COLOR_OPTIONS}>
                  {(opt) => (
                    <button
                      type="button"
                      onClick={() => setProjectColor(opt.value)}
                      class={cn(
                        "w-6.5 h-6.5 rounded-full border-2 transition-[transform,opacity,border-color] duration-200 will-change-transform cursor-pointer relative active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50",
                        projectColor() === opt.value
                          ? "border-white scale-110 shadow-md"
                          : "border-transparent opacity-60 hover:opacity-100 hover:scale-105",
                      )}
                      style={{ "background-color": opt.color }}
                      title={opt.label}
                    >
                      <Show when={projectColor() === opt.value}>
                        <div class="absolute inset-1 border border-black rounded-full" />
                      </Show>
                    </button>
                  )}
                </For>
              </div>
            </div>

            <div class="flex gap-3">
              <div class="space-y-1 flex-1">
                <label
                  for="new-project-rate"
                  class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
                >
                  {t(msg`Hourly Rate`)}
                </label>
                <input
                  id="new-project-rate"
                  type="number"
                  inputmode="decimal"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={projectHourlyRate()}
                  onInput={(e) => setProjectHourlyRate(e.currentTarget.value)}
                  data-testid="project-hourly-rate"
                  class="w-full bg-black/40 border border-white/10 rounded-[16px] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#afc6ff]/40 text-white placeholder-white/20 font-mono"
                />
              </div>
              <div class="space-y-1 w-24">
                <label
                  for="new-project-currency"
                  class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
                >
                  {t(msg`Currency`)}
                </label>
                <input
                  id="new-project-currency"
                  type="text"
                  maxlength="3"
                  placeholder="USD"
                  value={projectCurrency()}
                  onInput={(e) => setProjectCurrency(e.currentTarget.value.toUpperCase())}
                  data-testid="project-currency"
                  class="w-full bg-black/40 border border-white/10 rounded-[16px] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#afc6ff]/40 text-white placeholder-white/20 uppercase font-mono"
                />
              </div>
            </div>

            <label class="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={projectBillable()}
                onChange={(e) => setProjectBillable(e.currentTarget.checked)}
                data-testid="project-billable"
                class="h-4 w-4 rounded border-white/20 bg-black/40"
              />
              <span class="text-xs text-white/70 font-mono">{t(msg`Billable`)}</span>
            </label>

            <Show when={editingProjectId()}>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setVersionSheetOpen(true)}
                data-testid="open-rate-history"
                class="w-full justify-center gap-2 rounded-full font-mono text-xs uppercase tracking-wider border border-white/5 hover:bg-white/5"
              >
                <History class="h-4 w-4" />
                {t(msg`Rate history`)}
              </Button>
            </Show>

            <div class="flex justify-end gap-3 pt-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setIsOpen(false);
                  setEditingProjectId(null);
                  setDialogUrl({});
                }}
                class="px-5 py-2.5 rounded-full font-bold font-mono text-xs uppercase tracking-wider cursor-pointer border border-white/5 hover:bg-white/5"
              >
                {t(msg`Cancel`)}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting() || !drizzle()}
                class="px-6 py-2.5 bg-primary text-black rounded-full font-bold font-mono text-xs uppercase tracking-wider cursor-pointer shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] hover:bg-primary/95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Show when={!isSubmitting()} fallback={t(msg`Saving...`)}>
                  {editingProjectId() ? t(msg`Save`) : t(msg`Create`)}
                </Show>
              </Button>
            </div>
          </form>
        </div>
      </Dialog>

      {/* Historial de tarifas (PROJECT VERSIONING / rate-sealing) */}
      <Show when={editingProjectId()}>
        {(editingId) => (
          <ProjectVersionHistorySheet
            open={versionSheetOpen()}
            onOpenChange={setVersionSheetOpen}
            projectId={editingId}
            current={() => {
              const rate = projectHourlyRate().trim();
              const parsed = rate === "" ? null : Number(rate);
              return {
                name: projectName(),
                code: projectCode(),
                color: projectColor(),
                status: projectStatus(),
                hourlyRate: parsed !== null && Number.isFinite(parsed) ? parsed : null,
                currency: projectCurrency().trim() || null,
                billable: projectBillable(),
              };
            }}
            onRestore={handleRestoreVersion}
            restoring={restoringVersion}
          />
        )}
      </Show>

      {/* Tareas del proyecto */}
      <ProjectTasksDialog
        project={tasksProject()}
        open={Boolean(tasksProject())}
        onOpenChange={(open) => {
          if (!open) {
            setTasksProject(null);
            setDialogUrl({});
          }
        }}
      />

      {/* Confirmación de borrado */}
      <ConfirmDialog
        title={t(msg`Delete project?`)}
        description={(() => {
          const name = deleteTargetName();
          return t(
            msg`The project "${name}" will be removed for the whole team. Existing time entries are kept but left without a project.`,
          );
        })()}
        confirmLabel={t(msg`Delete`)}
        cancelLabel={t(msg`Cancel`)}
        variant="destructive"
        open={Boolean(deleteTarget())}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
            setDialogUrl({});
          }
        }}
        onConfirm={() => void handleDeleteConfirmed()}
      />
    </PageContainer>
  );
}

interface ProjectsSearch {
  q?: string;
  dialog?: "new" | "edit" | "tasks";
  confirm?: "delete";
  id?: string;
  wsdialog?: "new";
  /** Inherited from chronosRoute; we preserve it when rewriting the search. */
  ws?: string;
}

const isStr = (v: unknown): v is string => typeof v === "string" && v.trim() !== "";

export const chronosProjectsRoute = createRoute({
  getParentRoute: () => chronosRoute,
  path: "/projects",
  validateSearch: (raw: Record<string, unknown>): ProjectsSearch => {
    const dialog =
      raw.dialog === "new" || raw.dialog === "edit" || raw.dialog === "tasks"
        ? raw.dialog
        : undefined;
    const confirm = raw.confirm === "delete" ? raw.confirm : undefined;
    return {
      q: isStr(raw.q) ? raw.q : undefined,
      dialog,
      confirm,
      // `id` is only meaningful together with edit/tasks/delete.
      id:
        (dialog === "edit" || dialog === "tasks" || confirm) && isStr(raw.id) ? raw.id : undefined,
      wsdialog: raw.wsdialog === "new" ? "new" : undefined,
    };
  },
  component: ProjectsPage,
});
